import { Avatar, cn, Select, SelectItem, SelectProps } from '@heroui/react'
import { Field, FieldProps } from 'formik'
import { motion } from 'framer-motion'
import { useState } from 'react'

interface Option {
  key: string
  label: string
  flag?: string
}

type CustomSelectProps = Omit<
  SelectProps<Option>,
  'children' | 'selectedKeys' | 'onSelectionChange' | 'selectionMode' | 'onChange'
>

export interface SelectFormikProps extends CustomSelectProps {
  label: string
  options: Option[]
  name: string
  multiple?: boolean
  onChange?: (value: string | string[]) => void
  classNameContainer?: string
  variant?: 'underlined' | 'flat' | 'faded' | 'bordered'
  classNames?: {
    base?: string
    label?: string
    trigger?: string
    value?: string
    selectorIcon?: string
    popoverContent?: string
    [key: string]: string | undefined
  }
}

const SelectFormik = ({
  label,
  options,
  name,
  multiple,
  onChange,
  classNameContainer,
  classNames,
  ...props
}: SelectFormikProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <motion.div className={cn('w-full', classNameContainer)}>
      <Field name={name}>
        {({ field, form, meta }: FieldProps) => {
          const selectedValues = Array.isArray(field.value)
            ? field.value.map(String)
            : field.value
              ? [String(field.value)]
              : []
          const isInvalid = Boolean(meta.error && meta.touched)

          return (
            <div className="relative">
              <div
                className={cn(
                  'absolute top-0 left-4 w-full tr h-full flex items-center justify-end',
                )}
              >
                <div
                  className={cn(
                    'cursor-pointer transition-all duration-300',
                    isOpen && 'rotate-180',
                  )}
                />
              </div>
              <Select
                {...props}
                errorMessage={meta.error && meta.touched ? meta.error : ''}
                isInvalid={isInvalid}
                isOpen={isOpen}
                label={label}
                labelPlacement="outside"
                radius="sm"
                selectedKeys={new Set(selectedValues)}
                selectionMode={multiple ? 'multiple' : 'single'}
                variant="bordered"
                classNames={{
                  label: [
                    'text-gray-900',
                    'dark:text-foreground',
                    'group-data-[filled-within=true]:text-gray-900',
                    'dark:group-data-[filled-within=true]:text-white',
                    'group-data-[invalid=true]:text-danger',
                    'dark:group-data-[invalid=true]:text-danger',
                  ],
                  trigger: [
                    'bg-gray-50',
                    'dark:bg-[#1e1e20]',
                    'border-gray-200',
                    'dark:border-default-600',
                    'hover:bg-gray-100',
                    'dark:hover:bg-[#1e1e20]/70',
                    'group-data-[focus=true]:bg-gray-100',
                    'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
                    'group-data-[focus=true]:border-primary',
                    'dark:group-data-[focus=true]:border-primary',
                    '!cursor-text',
                  ],
                  ...classNames,
                }}
                onOpenChange={setIsOpen}
                onClose={() => {
                  setIsOpen(false)
                  form.setFieldTouched(field.name, true)
                }}
                onSelectionChange={keys => {
                  form.setFieldTouched(field.name, false)
                  const newValues = Array.from(keys).map(String)

                  form.setFieldValue(field.name, multiple ? newValues : newValues[0])
                  onChange?.(multiple ? newValues : newValues[0])
                }}
              >
                {options.map(option => (
                  <SelectItem
                    key={option.key}
                    startContent={
                      option.flag ? (
                        <Avatar
                          alt={option.label}
                          className="w-6 h-6"
                          src={`https://flagcdn.com/${option.flag}.svg`}
                        />
                      ) : undefined
                    }
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )
        }}
      </Field>
    </motion.div>
  )
}

export default SelectFormik
